import * as React from 'react'
import * as HoverCardPrimitive from '@radix-ui/react-hover-card'
import { cn } from '@/lib/utils'

/**
 * Figma (node 216:3126 — Hover Card / Card):
 * Container: bg-popover, border border-border (1px), rounded-lg (10px), p-2.5 (10px),
 *   shadow-md, w-80 (320px)
 *
 * Figma trigger states (node 216:3085):
 *   Default:  font-medium text-sm text-primary, h-8, px-2.5, py-2
 *   Hover:    + underline
 *   Focus:    + rounded-md, ring 3px rgba(163,163,163,0.5)
 *   Disabled: + opacity-50
 *   Pressed:  + opacity-60
 *
 * Animations: fade + zoom + slide per side
 */

const IntuiHoverCard = HoverCardPrimitive.Root

const IntuiHoverCardTrigger = React.forwardRef<
  React.ComponentRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <HoverCardPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex h-8 items-center justify-center px-2.5 py-2',
      'text-sm font-medium text-primary',
      'hover:underline',
      'focus-visible:outline-none focus-visible:rounded-md focus-visible:ring-[3px] focus-visible:ring-ring/50',
      'active:opacity-60',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    {...props}
  />
))
IntuiHoverCardTrigger.displayName = 'IntuiHoverCardTrigger'

export interface IntuiHoverCardContentProps
  extends React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content> {
  container?: HTMLElement | null
}

const IntuiHoverCardContent = React.forwardRef<
  React.ComponentRef<typeof HoverCardPrimitive.Content>,
  IntuiHoverCardContentProps
>(({ className, align = 'center', sideOffset = 4, container, ...props }, ref) => (
  <HoverCardPrimitive.Portal container={container}>
    <HoverCardPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-[999999] w-80 rounded-lg border border-border bg-popover p-2.5',
        'text-popover-foreground shadow-md outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </HoverCardPrimitive.Portal>
))
IntuiHoverCardContent.displayName = 'IntuiHoverCardContent'

export { IntuiHoverCard, IntuiHoverCardTrigger, IntuiHoverCardContent }
